import { useEffect, useState, useRef } from "react";
import AsyncSelect from "react-select/async";
import debounce from 'lodash/debounce';
import moment from "moment";
import api from "../../api.js";
import Menu from "../../components/Menu";
import ListCardProfessionalsReportPayments from "../../components/ListCardProfessionalsReportPayments.js";
import Report from '../../assets/img/report-black.svg';

function ReportPaymentSchedulings() {
  const [shopper, setShopper] = useState(null);
  const [professional, setProfessional] = useState(null);
  const [dateInitial, setDateInitial] = useState(moment().subtract(7, 'days').format('YYYY-MM-DD'));
  const [dateFinal, setDateFinal] = useState(moment().format('YYYY-MM-DD'));

  const [pageShopper, setPageShopper] = useState(1);
  const [pageProfessional, setPageProfessional] = useState(1);

  const [hasMoreShopper, setHasMoreShopper] = useState(true);
  const [hasMoreProfessional, setHasMoreProfessional] = useState(true);

  const [lastShopperInput, setLastShopperInput] = useState('');
  const [lastProfessionalInput, setLastProfessionalInput] = useState('');

  const abortControllerShopperRef = useRef(null); // AbortController para shoppers
  const abortControllerProfessionalRef = useRef(null); // AbortController para professionals

  const loadOptionsShopper = async (inputValue, callback) => {
    if (inputValue === lastShopperInput && !hasMoreShopper) return; // Verifica o input antes do hasMore

    // Cancela a requisição anterior se houver
    if (abortControllerShopperRef.current) {
      abortControllerShopperRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerShopperRef.current = abortController;

    try {
      const response = await api.get(`/api/shoppers/search/${inputValue}?page=${pageShopper}`, { signal: abortController.signal });
      const options = response.data.data.map(item => ({
        value: item.id,
        label: item.name,
      }));

      setLastShopperInput(inputValue);
      setHasMoreShopper(response.data.meta.last_page !== response.data.meta.current_page);
      setPageShopper(inputValue === lastShopperInput ? prevPage => prevPage + 1 : 1);

      callback(options);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Erro ao carregar opções paginadas de shoppers", error);
      }
    }
  };

  const loadOptionsProfessional = async (inputValue, callback) => {
    if (inputValue === lastProfessionalInput && !hasMoreProfessional) return; // Verifica o input antes do hasMore

    // Cancela a requisição anterior se houver
    if (abortControllerProfessionalRef.current) {
      abortControllerProfessionalRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerProfessionalRef.current = abortController;

    try {
      const response = await api.get(`/api/professionals/search/${inputValue}?page=${pageProfessional}`, { signal: abortController.signal });
      const options = response.data.data.map(item => ({
        value: item.id,
        label: item.name,
      }));

      setLastProfessionalInput(inputValue);
      setHasMoreProfessional(response.data.meta.last_page !== response.data.meta.current_page);
      setPageProfessional(inputValue === lastProfessionalInput ? prevPage => prevPage + 1 : 1);

      callback(options);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Erro ao carregar opções paginadas de profissionais", error);
      }
    }
  };

  useEffect(() => {
    const fetchInitialShopper = async () => {
      try {
        const response = await api.get('api/shoppers/listpage?page=1');
        const shopper = {
          value: response.data.data[0].id,
          label: response.data.data[0].name,
        };
        setShopper(shopper);
      } catch (error) {
        console.error('Erro ao carregar o shopper inicial', error);
      }
    };

    fetchInitialShopper();
  }, []);

  return (
    <Menu
      screen={
        <div className="screenContainer" style={{ paddingRight: '0px', maxHeight: '100vh', height: '100vh' }}>
          <div className="row row-container ">
            <h1 className="col-12">
              <img style={{ width: '40px' }} alt="svg" src={Report} />
              <span className="name-screen-title name-screen-professionals">Relatório de Pagamentos</span>
            </h1>

            <div style={{ display: 'flex', gap: 10, flexDirection: 'row', justifyContent: 'flex-start', flexWrap: 'wrap' }} className="col-12 name-search name-search-2">
              <div className="input-container" style={{ flexDirection: 'column', display: 'flex' }}>
                <label className="form-label-input">Data Inicial</label>
                <input
                  max={dateFinal}
                  style={{ width: 100 }}
                  value={dateInitial}
                  onChange={(e) => setDateInitial(e.target.value)}
                  type="date"
                  className="form-control input-pesquisa"
                />
              </div>

              <div className="input-container" style={{ flexDirection: 'column', display: 'flex' }}>
                <label className="form-label-input">Data Final</label>
                <input
                  min={dateInitial}
                  style={{ width: 100 }}
                  value={dateFinal}
                  onChange={(e) => setDateFinal(e.target.value)}
                  type="date"
                  className="form-control input-pesquisa"
                />
              </div>

              <div className="input-container" style={{ flexDirection: 'column', display: 'flex' }}>
                <label className="form-label-input">Profissional</label>
                <AsyncSelect

                  cacheOptions
                  loadOptions={loadOptionsProfessional}
                  onInputChange={debounce((inputValue) => {
                    setHasMoreProfessional(true);
                    setPageProfessional(1);
                    setLastProfessionalInput('');
                  }, 500)}
                  placeholder="Profissional"
                  noOptionsMessage={(value) => value.inputValue.length === 0 ? "Pesquise um profissional" : "Profissional não encontrado"}
                  isClearable
                  onChange={(selectedOption) => setProfessional(selectedOption)}
                  styles={{
                    container: (base) => ({ ...base, maxWidth: '250px', minWidth: '250px' }),
                    control: (base) => ({ ...base, height: '44px', fontSize: '17px', fontWeight: '500', borderRadius: '100px' }),
                    placeholder: (base) => ({ ...base, color: 'black' }),
                  }}
                />
              </div>

              <div className="input-container" style={{ flexDirection: 'column', display: 'flex' }}>
                <label className="form-label-input">Empresa</label>
                <AsyncSelect
                  cacheOptions
                  loadOptions={loadOptionsShopper}
                  onInputChange={debounce((inputValue) => {
                    setHasMoreShopper(true);
                    setPageShopper(1);
                    setLastShopperInput('');
                  }, 500)}
                  placeholder="Pesquisar Empresa"
                  value={shopper}
                  noOptionsMessage={(value) => value.inputValue.length === 0 ? "Pesquise uma empresa" : "Empresa não encontrada"}
                  onChange={(selectedOption) => setShopper(selectedOption)}
                  styles={{
                    container: (base) => ({ ...base, maxWidth: '250px', minWidth: '250px' }),
                    control: (base) => ({ ...base, height: '44px', fontSize: '17px', fontWeight: '500', borderRadius: '100px' }),
                    placeholder: (base) => ({ ...base, color: 'black' }),
                  }}
                />
              </div>
            </div>
            <div className="row row-subcontainer ">
              <div className='col-12' style={{ padding: '0px 10px', cursor: 'pointer' }}>
                <div>
                  <div style={{ position: 'relative', alignItems: 'center', padding: 5 }} className='row modal-top-table'>
                    <div style={{ margin: 'auto', alignItems: 'center' }} className='d-flex col-12 col-sm-1 flex-column'>
                    </div>

                    <div className='d-flex col-12 col-sm-4 flex-column'>
                      <span style={{ color: 'black' }} className='card-list-title'>paciente</span>
                    </div>
                    <div className='d-flex col-12 col-sm-3 flex-column'>
                      <span style={{ color: 'black' }} className='card-list-title'>agendamento</span>
                    </div>
                    <div className='d-flex col-12 col-sm-4 flex-column'>
                      <span style={{ color: 'black' }} className='card-list-title'>faturamento</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ padding: "0px 4px", paddingBottom: 120 }} className="row row-subcontainer row-subcontainer-list-payment">
              <ListCardProfessionalsReportPayments search={""} initialDate={dateInitial} finalDate={dateFinal} professional={professional} shopper={shopper} />
            </div>
          </div>
        </div>
      }
    />
  );
}

export default ReportPaymentSchedulings;
